"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";
import dayjs from "dayjs";
import { datadogLogs } from "@datadog/browser-logs";
import { DEFAULT_SUF_QUIZ_VERSION } from "@api-clients/suf-quiz-answers";

import type { SignupFlowStore } from "../signupFlowStore.types";
import {
  defaultDelivery,
  defaultSubscription,
  defaultUser,
  enrichZodIssues,
  parseSignupFlowPersistedSnapshot,
  type TAgeViewState,
  type TCatEntry,
  type TCatFormDataInput,
  type TSignupFlowStoreData,
  type TViewState,
} from "@./suf-store";
import {
  defaultViewState,
  defaultAgeViewState,
  SIGNUP_FLOW_TTL_DAYS,
} from "../constants";
import {
  calculateCatBirthday,
  recalculateCatCalories,
  reEstimateCatWeights,
} from "../utils";
import { buildBaseStoreSlice } from "../../../shared/storeHelpers";

import { useSignupFlowErrorStore } from "./useSignUpFlowErrorStore";

/**
 * Zustand store for managing sign-up flow data: cats, user, delivery, and subscription.
 */
export const useSignupFlowStore = create<SignupFlowStore>()(
  persist(
    (set, get) => ({
      ...buildBaseStoreSlice(set, get),

      createCat: (id, cat) => set((state) => {
        if (state.cats[id]) { return {}; }
        return {
          cats: {
            ...state.cats,
            [id]: { ...cat },
          },
          viewState: {
            ...state.viewState,
            age: {
              ...state.viewState.age,
              [id]: defaultAgeViewState,
            },
          },
        };
      }),

      createCats: (entries: TCatEntry[]) => set((state) => {
        const updatedCats: Record<string, TCatFormDataInput> = { ...state.cats };
        const updatedAgeViewState = { ...state.viewState.age };
        const incomingIds = new Set(entries.map((e) => e.id));

        // Remove cats and their view states that are no longer in the incoming entries
        for (const existingId of Object.keys(updatedCats)) {
          if (!incomingIds.has(existingId)) {
            delete updatedCats[existingId];
            delete updatedAgeViewState[existingId];
          }
        }

        // Add/update cats and initialize their age view states
        for (const { id, cat } of entries) {
          updatedCats[id] = {
            ...updatedCats[id],
            ...cat,
          };
          // Initialize age view state for new cats
          if (!updatedAgeViewState[id]) {
            updatedAgeViewState[id] = defaultAgeViewState;
          }
        }

        const firstCatId = entries[0]?.id ?? "";

        return {
          cats: updatedCats,
          activeCatId: firstCatId,
          viewState: {
            ...state.viewState,
            age: updatedAgeViewState,
          },
        };
      }),

      /* --- View State ---*/
      viewState: defaultViewState,
      updateViewState: (
        view: string,
        params: Partial<TViewState[keyof TViewState]> | Partial<TAgeViewState>,
        catId?: string,
      ) => set((state) => {
        const viewState = { ...state.viewState };

        if (view === "age" && catId) {
          // Update age viewState for specific cat
          viewState.age = {
            ...viewState.age,
            [catId]: {
              ...viewState.age[catId],
              ...params,
            } as TAgeViewState,
          };
        } else if (view === "catParentDetails") {
          viewState.catParentDetails = { ...viewState.catParentDetails, ...params };
        } else if (view === "checkout") {
          // Update global checkout viewState
          viewState.checkout = { ...viewState.checkout, ...params };

          // Keep special logic for checkout's isDeliveryComplete

          if ("isDeliveryComplete" in params && params.isDeliveryComplete === true) {
            viewState.checkout.hasCompletedDeliveryOnce = true;
          }
        }

        return { viewState };
      }),

      updateFeatureFlags: (flags) => set((state) => ({
        featureFlags: {
          ...state.featureFlags,
          ...flags,
        },
      })),

      /* --- Reset ---*/
      resetSignupFlow: () => set({
        featureFlags: {},
        cats: {},
        activeCatId: "",
        user: defaultUser,
        delivery: defaultDelivery,
        subscription: defaultSubscription,
        viewState: defaultViewState,
        sufQuizVersion: DEFAULT_SUF_QUIZ_VERSION,
        versionMismatchDetected: false, // Clear flag on reset
        savedAt: dayjs().format(), // Restart TTL timer
      }),

      /* --- Birthday calculation ---*/
      calculateAllCatsBirthdays: () => set((state) => {
        const updatedCats = { ...state.cats };

        Object.entries(updatedCats).forEach(([ catId, cat ]) => {
          if (!cat.softDelete) {
            const calculatedBirthday = calculateCatBirthday(cat);
            if (calculatedBirthday) {
              updatedCats[catId] = {
                ...cat,
                birthday: calculatedBirthday,
              };
            }
          }
        });

        return { cats: updatedCats };
      }),


      /* --- Version ---*/
      sufQuizVersion: DEFAULT_SUF_QUIZ_VERSION,

      featureFlags: {},

      /* --- TTL ---*/
      savedAt: dayjs().format(),
    }),
    {
      name: "suf-store",
      // Every exit path must set isStoreInitialised; consumers block first render on it.
      // Both recovery paths reset the store, so tightening the Zod schema wipes
      // in-progress signups already saved in browsers.
      onRehydrateStorage: () => (state, rehydrationError) => {
        if (!state) {
          datadogLogs?.logger?.error("[suf-store] Storage rehydration failed", {
            rehydrationError,
          });
          queueMicrotask(() => {
            useSignupFlowStore.setState({ isStoreInitialised: true });
          });
          return;
        }

        try {
          // --- 1. TTL — clear stale local state (same recovery path as an invalid snapshot below).
          if (
            state.savedAt &&
            dayjs(state.savedAt).isBefore(dayjs().subtract(SIGNUP_FLOW_TTL_DAYS, "day"))
          ) {
            state.resetSignupFlow();
            useSignupFlowErrorStore.getState().clearAll();
            queueMicrotask(() => state.setIsStoreInitialised(true));
            return;
          }

          // --- 2. Parse persisted snapshot with relevant state properties (no methods)
          // with libs/suf-store (Zod) for validation and normalisation.
          // Allowlist: new state fields must be added here and to signupFlowStoreSchema.
          const persistedSlice = {
            isStoreInitialised: state.isStoreInitialised,
            versionMismatchDetected: state.versionMismatchDetected,
            featureFlags: state.featureFlags,
            cats: state.cats,
            activeCatId: state.activeCatId,
            user: state.user,
            delivery: state.delivery,
            subscription: state.subscription,
            viewState: state.viewState,
            isLoading: state.isLoading,
            sufQuizVersion: state.sufQuizVersion,
            savedAt: state.savedAt,
          };

          const parsed = parseSignupFlowPersistedSnapshot(persistedSlice);
          if (!parsed.success) {
            // Corrupt or incompatible persisted shape — log to Datadog, reset store and clear errors.
            datadogLogs?.logger?.warn("[suf-store] Persisted snapshot failed validation", {
              issues: enrichZodIssues(parsed.error.issues, persistedSlice),
              activeCatId: state.activeCatId,
              catCount: Object.keys(state.cats ?? {}).length,
              userId: state.user?.id,
              legacyCustomerId: state.user?.oldId,
            });
            state.resetSignupFlow();
            useSignupFlowErrorStore.getState().clearAll();
            queueMicrotask(() => state.setIsStoreInitialised(true));
            return;
          }

          // --- 3. Apply parsed state to the store on success.
          // Check for version mismatch and set flag (don't reset here)
          // Reset logic is handled explicitly in AC page + Layout useEffect
          const next: TSignupFlowStoreData = {
            ...parsed.data,
            cats: recalculateCatCalories(reEstimateCatWeights(parsed.data.cats)),
            ...(parsed.data.sufQuizVersion !== DEFAULT_SUF_QUIZ_VERSION && {
              versionMismatchDetected: true,
            }),
          };

          // --- 4. Defer setState so the store variable is assigned (avoids TDZ during create())
          // and defer savedAt reset and setIsStoreInitialised so sync persist doesn't overwrite them.
          queueMicrotask(() => {
            useSignupFlowStore.setState({
              ...next,
              savedAt: dayjs().format(),
            } as Partial<SignupFlowStore>);
            state.setIsStoreInitialised(true);
          });
        } catch (error) {
          datadogLogs?.logger?.error("[suf-store] Rehydration failed", { error });
          state.resetSignupFlow();
          useSignupFlowErrorStore.getState().clearAll();
          queueMicrotask(() => state.setIsStoreInitialised(true));
        }
      },
    },
  ),
);
